import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { BrandLogo } from "./Logo";

export const scrollToId = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LangToggle = ({ lang, setLang, dark = false }) => (
  <div
    className={`flex items-center rounded-full border p-0.5 font-mono text-[11px] tracking-widest ${
      dark ? "border-white/20 text-stone-300" : "border-black/15 text-stone-600"
    }`}
    data-testid="lang-toggle"
  >
    {["es", "en"].map((l) => (
      <button
        key={l}
        data-testid={`lang-toggle-${l}`}
        onClick={() => setLang(l)}
        className={`rounded-full px-2.5 py-1 uppercase transition-colors duration-300 ${
          lang === l ? "bg-terracotta text-bone" : "hover:text-terracotta"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { hash: "#servicios", label: t.nav.services, id: "services" },
    { hash: "#proyectos", label: t.nav.projects, id: "projects" },
    { hash: "#nosotros", label: t.nav.about, id: "about" },
    { hash: "#presupuesto", label: t.nav.quote, id: "quote" },
  ];

  const go = (hash) => {
    setOpen(false);
    setTimeout(() => scrollToId(hash), open ? 250 : 0);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "border-b border-black/10 bg-bone/85 backdrop-blur-xl"
          : "border-b border-white/10 bg-charcoal/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-10">
        <button data-testid="brand-home-btn" onClick={() => go("#inicio")}>
          <BrandLogo dark={!scrolled} />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.hash)}
              className={`font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 hover:text-terracotta ${
                scrolled ? "text-stone-600" : "text-stone-300"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle lang={lang} setLang={setLang} dark={!scrolled} />
          <a
            data-testid="header-call-btn"
            href="tel:+34622669378"
            className="hidden items-center gap-2 rounded-full bg-terracotta px-4 py-2 font-mono text-[11px] tracking-widest text-bone transition-colors duration-300 hover:bg-terra-dark md:flex"
          >
            <Phone size={13} strokeWidth={2.2} />
            {t.nav.call}
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className={`lg:hidden ${scrolled ? "text-charcoal" : "text-bone"}`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-drawer"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-black/10 bg-bone/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                  onClick={() => go(l.hash)}
                  className="border-b border-black/5 py-3 text-left font-display text-2xl text-charcoal"
                >
                  {l.label}
                </motion.button>
              ))}
              <a
                data-testid="mobile-call-btn"
                href="tel:+34622669378"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-terracotta px-4 py-3 font-mono text-xs tracking-widest text-bone"
              >
                <Phone size={14} /> +34 622 66 93 78
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
